/**
 * Cost-panel presentation rules for generated Pro Project packages.
 *
 * Two defects motivated pulling this out of DesignStudio so it could be tested
 * directly, both observed on a live generation:
 *
 *   - every cost figure rendered as a bare number, with no currency anywhere in
 *     the panel, so "39.43" and "15,000" had no unit at all
 *   - the panel showed a green "Reconciled to BOM" badge while the package-level
 *     quality gate was simultaneously reporting a cost_analysis deficit, so the
 *     two halves of the same screen contradicted each other about cost
 */

/** Deficits the package-level quality gate raised against cost_analysis. */
export function costGateDeficits(qualityWarnings) {
  if (!Array.isArray(qualityWarnings)) return [];
  return qualityWarnings
    .map((warning) => String(warning))
    .filter((warning) => warning.startsWith('cost_analysis'));
}

/**
 * The currency the package states, or null when it stated none.
 *
 * Never guesses a default: silently labelling unknown figures "USD" would invent
 * a financial fact the generation did not provide.
 */
export function costCurrency(costAnalysis) {
  const currency = costAnalysis && costAnalysis.currency;
  if (typeof currency !== 'string') return null;
  const trimmed = currency.trim();
  return trimmed ? trimmed : null;
}

/** Format one cost figure, carrying the currency when it is known. */
export function formatCostValue(value, currency) {
  if (!Number.isFinite(value)) return 'TBD';
  const amount = value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return currency ? `${currency} ${amount}` : amount;
}

/**
 * Whether the panel may present these figures as reconciled to the BOM.
 *
 * A cost deficit from the quality gate demotes the badge even when the local
 * arithmetic checks out, so the panel can never look calmer than the gate.
 */
export function isCostReconciled({
  costAnalysis,
  hasTbdCost,
  costDiscrepancies,
  qualityWarnings,
} = {}) {
  const basisReconciled = Boolean(costAnalysis)
    && costAnalysis.materials_cost_basis === 'sum_of_bom_line_costs';
  const discrepancies = Array.isArray(costDiscrepancies) ? costDiscrepancies.length : 0;
  return Boolean(
    basisReconciled
    && !hasTbdCost
    && !discrepancies
    && !costGateDeficits(qualityWarnings).length,
  );
}
